export const SITE_URL = "https://www.hiveworkspaces.com";
export const BRAND = "The Hive Workspaces";
export const BRAND_LOGO = `${SITE_URL}/Hive-Favicon.png`;
export const BRAND_DESCRIPTION =
  "Enterprise-grade managed office spaces, coworking spaces, private cabins, meeting rooms and virtual offices across India.";
export const BRAND_PHONE = "+91 7022274000";
export const BRAND_EMAIL = "hello@hiveworkspaces.com";

// Canonical NAP — byte-identical to Footer and Contact page
export const BRAND_ADDRESS = {
  streetAddress: "L5, VR Bangalore, Mahadevpura",
  addressLocality: "Bangalore",
  addressRegion: "Karnataka",
  postalCode: "560048",
  addressCountry: "IN",
};
export const BRAND_ADDRESS_TEXT = "L5, VR Bangalore, Mahadevpura. Bangalore, Karnataka 560048, IN.";

// Canonical publish/modified dates for evergreen pages (visible + schema)
export const SITE_PUBLISHED = "2011-01-01T00:00:00.000Z";
export const SITE_MODIFIED = "2026-05-03T00:00:00.000Z";

export const SAME_AS = [
  "https://www.linkedin.com/company/hiveworkspaces",
  "https://www.instagram.com/hiveworkspaces/",
  "https://www.facebook.com/hiveworkspaces/",
  "https://www.youtube.com/@thehiveflexibleworkspaces",
];

const AREA_SERVED = [
  { "@type": "City", name: "Chennai" },
  { "@type": "City", name: "Bangalore" },
  { "@type": "City", name: "Hyderabad" },
  { "@type": "City", name: "Pune" },
  { "@type": "City", name: "Mumbai" },
  { "@type": "City", name: "Delhi" },
  { "@type": "City", name: "Noida" },
];

const KNOWS_ABOUT = [
  "Managed offices",
  "Coworking spaces",
  "Private cabins",
  "Dedicated desks",
  "Hot desks",
  "Meeting rooms",
  "Event spaces",
  "Virtual offices",
  "Enterprise office solutions",
  "Flexible workspace India",
];

/** Canonical Organization entity — reuse everywhere so assistants see one consistent entity.
 * NAP is byte-identical to Footer/Contact visible text for model confidence.
 */
export const organizationSchema = () => ({
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: BRAND,
  alternateName: "The Hive",
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: BRAND_LOGO,
    width: 512,
    height: 512,
  },
  description: BRAND_DESCRIPTION,
  telephone: BRAND_PHONE,
  email: BRAND_EMAIL,
  address: {
    "@type": "PostalAddress",
    streetAddress: BRAND_ADDRESS.streetAddress,
    addressLocality: BRAND_ADDRESS.addressLocality,
    addressRegion: BRAND_ADDRESS.addressRegion,
    postalCode: BRAND_ADDRESS.postalCode,
    addressCountry: BRAND_ADDRESS.addressCountry,
  },
  areaServed: AREA_SERVED,
  sameAs: SAME_AS,
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: BRAND_PHONE,
      email: BRAND_EMAIL,
      contactType: "sales",
      areaServed: "IN",
      availableLanguage: ["English", "Hindi"],
    },
  ],
  knowsAbout: KNOWS_ABOUT,
});

export const websiteSchema = () => ({
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: BRAND,
  url: SITE_URL,
  inLanguage: "en-IN",
  publisher: { "@id": `${SITE_URL}/#organization` },
});

/** Reference to the canonical organization without duplicating it inside a @graph. */
export const orgRef = () => ({ "@id": `${SITE_URL}/#organization` });

/** Author removed per request — keep empty placeholder if schema requires it */
export const AUTHOR_PERSON = {
  "@type": "Person",
  name: "",
};

export const authorPersonSchema = () => ({
  "@type": "Person",
  name: "",
});

/**
 * FAQPage — pass [{ q, a }].
 * Answers should be 2-3 self-contained sentences, plain text (no HTML), matching visible page content.
 */
export const faqPageSchema = (faqs) => ({
  "@type": "FAQPage",
  "@id": `${SITE_URL}/#faq`,
  inLanguage: "en-IN",
  mainEntity: faqs.map(({ q, a }) => ({
    "@type": "Question",
    name: q.trim(),
    acceptedAnswer: {
      "@type": "Answer",
      text: a.trim(),
      inLanguage: "en-IN",
    },
  })),
});

/**
 * BreadcrumbList — pass ordered items [{ name, url }] with absolute URLs.
 */
export const breadcrumbSchema = (items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: item.url,
  })),
});

/**
 * Service — describes what you sell / what you do (no price).
 * Used to satisfy GEO "Product or Service schema" without exposing pricing.
 */
export const serviceSchema = ({ name, description, url, serviceType, category, image }) => {
  return {
    "@type": "Service",
    name,
    description,
    url,
    serviceType: serviceType || name,
    provider: orgRef(),
    areaServed: AREA_SERVED.map((c) => c.name),
    ...(category ? { category } : {}),
    ...(image ? { image: Array.isArray(image) ? image : [image] } : {}),
  };
};

/**
 * Product — describes what you sell (no price per request).
 * Includes name + description + brand + category + image + url for GEO.
 */
export const productSchema = ({
  name,
  description,
  url,
  image,
  sku,
  brandName = BRAND,
  category,
  slug,
}) => {
  const cleanDesc = description ? description.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 500) : undefined;
  return {
    "@type": "Product",
    "@id": url ? `${url}#product` : undefined,
    name,
    description: cleanDesc,
    url,
    image: image ? (Array.isArray(image) ? image : [image]) : undefined,
    sku: sku || slug,
    brand: { "@type": "Brand", name: brandName },
    category,
  };
};

/**
 * AggregateOffer — use when a Service has multiple priced variants (e.g. day vs month).
 */
export const aggregateOfferSchema = ({ lowPrice, highPrice, priceCurrency = "INR", url, offerCount }) => ({
  "@type": "AggregateOffer",
  priceCurrency,
  lowPrice: String(lowPrice),
  highPrice: String(highPrice),
  url,
  availability: "https://schema.org/InStock",
  seller: orgRef(),
  ...(offerCount ? { offerCount } : {}),
});

/**
 * LocalBusiness — per branch/city page.
 */
export const localBusinessSchema = ({
  name,
  description,
  url,
  telephone,
  email,
  streetAddress,
  addressLocality,
  image,
  latitude,
  longitude,
}) => ({
  "@type": "LocalBusiness",
  "@id": `${url}#localbusiness`,
  name,
  description,
  url,
  telephone: telephone || BRAND_PHONE,
  email: email || BRAND_EMAIL,
  parentOrganization: orgRef(),
  image: image ? [image].flat() : undefined,
  priceRange: "₹₹",
  openingHours: "Mo-Su 00:00-24:00",
  address: {
    "@type": "PostalAddress",
    streetAddress: streetAddress || "",
    addressLocality,
    addressCountry: "IN",
  },
  geo: latitude && longitude
    ? { "@type": "GeoCoordinates", latitude, longitude }
    : undefined,
});

/**
 * BlogPosting — dates and publisher; author removed per request (empty if required).
 */
export const blogPostingSchema = ({
  title,
  description,
  url,
  image,
  datePublished,
  dateModified,
  section,
  keywords = [],
  authorName,
  authorJobTitle,
}) => {
  const toISO = (d) => {
    if (!d) return undefined;
    try {
      const date = new Date(d);
      return isNaN(date.getTime()) ? d : date.toISOString();
    } catch {
      return d;
    }
  };
  // Author removed — keep empty if caller still passes a name, otherwise omit/empty
  const author =
    authorName && authorName.trim()
      ? {
          "@type": "Person",
          name: "",
        }
      : {
          "@type": "Person",
          name: "",
        };

  return {
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    headline: title,
    description,
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    image: image ? [image].flat() : undefined,
    datePublished: toISO(datePublished),
    dateModified: toISO(dateModified || datePublished),
    articleSection: section,
    keywords: keywords.length ? keywords.join(", ") : undefined,
    inLanguage: "en-IN",
    author,
    publisher: {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: BRAND,
      logo: { "@type": "ImageObject", url: BRAND_LOGO, width: 512, height: 512 },
    },
    isPartOf: { "@id": `${SITE_URL}/#website` },
  };
};

/**
 * Generic Article schema — author removed per request.
 */
export const articleSchema = ({
  headline,
  description,
  url,
  image,
  datePublished,
  dateModified,
  authorName,
  authorJobTitle,
}) =>
  blogPostingSchema({
    title: headline,
    description,
    url,
    image,
    datePublished,
    dateModified,
    section: undefined,
    keywords: [],
    authorName: "",
    authorJobTitle: "",
  });
